import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, Plus, X, Loader2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GOOGLE_MAPS_URL, GOOGLE_WRITE_REVIEW_URL } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: "", place: "", rating: 5, text: "" };

const GoogleGIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const initialsAvatar = (name) => {
  const parts = (name || "?").trim().split(/\s+/);
  const letters = ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
  return letters;
};

const ReviewCard = ({ review, isGoogle, index }) => {
  return (
    <div
      data-testid={`testimonial-${index}`}
      className="brut-card p-7 flex flex-col relative"
    >
      {isGoogle && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-full px-2.5 py-1">
          <GoogleGIcon size={12} />
          <span className="font-body text-[10px] font-semibold tracking-wide text-[#4B5563] uppercase">Google</span>
        </div>
      )}
      <Quote size={28} className="text-[#D92525]" strokeWidth={2.2} />
      <p className="font-body text-[#0B132B] mt-4 leading-relaxed text-base flex-1">
        &ldquo;{review.text}&rdquo;
      </p>
      <div className="mt-6 pt-5 border-t-2 border-[#0B132B] flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {isGoogle && review.author_photo ? (
            <img
              src={review.author_photo}
              alt={review.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-[#0B132B]"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#0B132B] text-white font-display font-black text-sm flex items-center justify-center shrink-0">
              {initialsAvatar(review.name)}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display font-bold text-[#0B132B] truncate">{review.name}</div>
            {(review.place || review.relative_time) && (
              <div className="font-body text-xs text-[#4B5563] mt-0.5 truncate">
                {review.place || review.relative_time}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-0.5 shrink-0">
          {Array.from({ length: 5 }).map((_, k) => (
            <Star
              key={k}
              size={14}
              fill={k < (review.rating || 5) ? "#D92525" : "transparent"}
              stroke="#D92525"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [google, setGoogle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [localRes, gRes] = await Promise.allSettled([
        axios.get(`${API}/reviews`),
        axios.get(`${API}/google-reviews`),
      ]);
      if (localRes.status === "fulfilled") setReviews(localRes.value.data);
      if (gRes.status === "fulfilled") setGoogle(gRes.value.data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error("Fyll i namn och recension.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Tack för din recension!");
      setForm(empty);
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Kunde inte skicka recensionen. Försök igen.");
    } finally {
      setSubmitting(false);
    }
  };

  // Merge Google reviews first, then local ones (dedupe not needed — different sources)
  const combined = useMemo(() => {
    const items = [];
    if (google?.reviews?.length) {
      for (const r of google.reviews) {
        items.push({
          key: `g-${r.author_name}-${r.publish_time}`,
          isGoogle: true,
          data: {
            name: r.author_name,
            author_photo: r.author_photo,
            rating: r.rating,
            text: r.text,
            relative_time: r.relative_time,
          },
        });
      }
    }
    for (const r of reviews) {
      items.push({
        key: `l-${r.id}`,
        isGoogle: false,
        data: {
          name: r.name,
          place: r.place,
          rating: r.rating,
          text: r.text,
        },
      });
    }
    return items;
  }, [google, reviews]);

  const aggregateRating = google?.rating;
  const aggregateCount = google?.user_ratings_total || 0;
  const mapsUri = GOOGLE_MAPS_URL;
  const writeReviewUri = GOOGLE_WRITE_REVIEW_URL;

  return (
    <section
      id="omdomen"
      data-testid="testimonials-section"
      className="py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#FFB3B3]">
              <span className="w-8 h-[2px] bg-[#FFB3B3]" /> 05 — Omdömen
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white mt-3 leading-[1]">
              Vad våra
              <br />
              <span className="text-[#FFB3B3]">kunder säger.</span>
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {writeReviewUri && (
              <a
                data-testid="write-google-review-button"
                href={writeReviewUri}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white hover:bg-[#FFF5F5] text-[#0B132B] px-6 py-4 font-display font-bold text-sm tracking-[0.15em] uppercase border-2 border-white transition-all group"
              >
                <GoogleGIcon size={18} />
                Recensera på Google
                <ExternalLink size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
            )}
            <a
              href={writeReviewUri}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="add-review-button"
              className="inline-flex items-center justify-center gap-3 bg-[#D92525] hover:bg-[#B91C1C] text-white px-6 py-4 font-display font-bold text-sm tracking-[0.15em] uppercase border-2 border-[#D92525] hover:border-[#B91C1C] transition-all group"
            >
              <Plus size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform" />
              Lämna en recension
            </a>
          </div>
        </div>

        {/* Google aggregate rating badge */}
        {aggregateRating != null && aggregateCount > 0 && (
          <a
            href={mapsUri || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-rating-badge"
            className="group inline-flex flex-wrap items-center gap-x-4 gap-y-2 max-w-full bg-white border-2 border-[#0B132B] px-5 py-3 mb-10 hover:bg-[#FFF5F5] transition-colors"
          >
            <GoogleGIcon size={22} />
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-2xl text-[#0B132B] leading-none">
                {Number(aggregateRating).toFixed(1)}
              </span>
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star
                    key={k}
                    size={14}
                    fill={k < Math.round(aggregateRating) ? "#D92525" : "transparent"}
                    stroke="#D92525"
                  />
                ))}
              </div>
            </div>
            <div className="hidden sm:block h-6 w-px bg-[#0B132B]/20" />
            <span className="font-body text-sm text-[#4B5563]">
              Baserat på <b className="text-[#0B132B]">{aggregateCount}</b> Google-recensioner
            </span>
            <ExternalLink size={14} className="text-[#4B5563] group-hover:translate-x-0.5 transition-transform" />
          </a>
        )}

        {loading ? (
          <div data-testid="reviews-loading" className="font-body text-white/70">
            Laddar omdömen…
          </div>
        ) : combined.length === 0 ? (
          <div
            data-testid="reviews-empty"
            className="border-2 border-dashed border-white/25 p-10 md:p-14 text-center bg-white/[0.03]"
          >
            <Quote size={36} className="text-[#D92525] mx-auto" strokeWidth={2.2} />
            <h3 className="font-display font-black text-2xl md:text-3xl text-white mt-4">
              Bli den första att skriva en recension
            </h3>
            <p className="font-body text-white/70 mt-2 max-w-lg mx-auto">
              Har du anlitat oss? Dela din upplevelse och hjälp andra att välja rätt hantverkare.
            </p>
            <button
              data-testid="add-review-empty-cta"
              onClick={() => setOpen(true)}
              className="mt-6 inline-flex items-center gap-2 border-2 border-white text-white px-5 py-3 font-display font-bold text-xs tracking-[0.15em] uppercase hover:bg-white hover:text-[#0B132B] transition-colors"
            >
              <Plus size={16} strokeWidth={3} /> Skriv recension
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {combined.map((item, i) => (
              <ReviewCard
                key={item.key}
                review={item.data}
                isGoogle={item.isGoogle}
                index={i}
              />
            ))}
          </div>
        )}

        {mapsUri && (
          <div className="mt-10 flex justify-center">
            <a
              href={mapsUri}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="see-all-google-reviews"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-display font-bold text-xs tracking-[0.15em] uppercase border-b-2 border-white/30 hover:border-white pb-1 transition-colors"
            >
              Se alla recensioner på Google
              <ExternalLink size={14} />
            </a>
          </div>
        )}
      </div>

      {/* Add review dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="review-dialog"
          className="max-w-lg w-[95vw] p-0 bg-white border-2 border-[#0B132B]"
        >
          <div className="flex items-center justify-between border-b-2 border-[#0B132B] px-6 py-4">
            <div>
              <DialogTitle className="font-display font-black text-2xl text-[#0B132B] tracking-tight">
                Lämna en recension
              </DialogTitle>
              <DialogDescription className="font-body text-xs text-[#4B5563] mt-1">
                Din feedback hjälper oss att bli bättre.
              </DialogDescription>
            </div>
            <button
              data-testid="review-dialog-close"
              onClick={() => setOpen(false)}
              className="p-2 border-2 border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white transition-colors"
              aria-label="Stäng"
            >
              <X size={16} />
            </button>
          </div>
          <form onSubmit={submit} className="p-6 grid gap-4">
            {writeReviewUri && (
              <a
                href={writeReviewUri}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="review-dialog-google-link"
                className="flex items-center justify-between gap-3 bg-[#FFF5F5] hover:bg-[#FFE5E5] border-2 border-[#0B132B] px-4 py-3 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <GoogleGIcon size={20} />
                  <div>
                    <div className="font-display font-bold text-sm text-[#0B132B]">
                      Recensera oss på Google
                    </div>
                    <div className="font-body text-xs text-[#4B5563]">
                      Hjälper oss synas bättre online.
                    </div>
                  </div>
                </div>
                <ExternalLink size={16} className="text-[#0B132B] group-hover:translate-x-0.5 transition-transform" />
              </a>
            )}
            <div>
              <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                Namn *
              </label>
              <input
                data-testid="review-input-name"
                className="swed-input"
                type="text"
                placeholder="Ditt namn"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                Ort (valfritt)
              </label>
              <input
                data-testid="review-input-place"
                className="swed-input"
                type="text"
                placeholder="t.ex. Eskilstuna"
                value={form.place}
                onChange={(e) => setForm({ ...form, place: e.target.value })}
              />
            </div>
            <div>
              <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                Betyg *
              </label>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`review-star-${k + 1}`}
                    onClick={() => setForm({ ...form, rating: k + 1 })}
                    className="p-1"
                    aria-label={`${k + 1} stjärnor`}
                  >
                    <Star
                      size={28}
                      strokeWidth={2}
                      fill={k < form.rating ? "#D92525" : "transparent"}
                      stroke="#D92525"
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-display font-bold text-xs uppercase tracking-[0.15em] text-[#0B132B] mb-1.5">
                Recension *
              </label>
              <textarea
                data-testid="review-input-text"
                className="swed-input"
                rows={5}
                placeholder="Berätta om din upplevelse..."
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                required
              />
            </div>
            <button
              data-testid="review-submit"
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Skickar...
                </>
              ) : (
                "Skicka recension"
              )}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
